import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { focusMainContent } from "@/lib/focusMain";

/**
 * On client-side route changes, reset scroll to the top and move keyboard focus
 * to the start of the main content so screen-reader and keyboard users land on
 * the new page instead of being stranded where the previous page's focus was.
 * (WCAG 2.4.3 Focus Order — react-router does not do this automatically.)
 *
 * Does nothing on the initial page load: the browser already has focus at the
 * top of the document (or on a #hash target), and moving it into <main> there
 * would make the first Tab skip the skip link and the whole nav (WCAG 2.4.1 —
 * NCS-327). Honours prefers-reduced-motion for the scroll.
 */
export function RouteFocus() {
  const { pathname, hash } = useLocation();
  // The last location this effect handled. Comparing locations (rather than a
  // "first render" boolean) also stays correct under StrictMode, which runs the
  // effect twice on mount with the same location.
  const lastLocation = useRef<string | null>(null);

  useEffect(() => {
    const location = pathname + hash;
    const isInitialLoad = lastLocation.current === null;
    const unchanged = lastLocation.current === location;
    lastLocation.current = location;
    if (isInitialLoad || unchanged) return;

    // When the URL carries a hash (e.g. "/#services"), the target section owns
    // focus and scroll — move focus there and let the browser anchor to it,
    // instead of stealing focus/scroll back to the top.
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
        target.focus({ preventScroll: true });
        target.scrollIntoView();
        return;
      }
    }

    focusMainContent({ scroll: false });

    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  }, [pathname, hash]);

  return null;
}
